import { useEffect, useRef } from 'react';
import { toast } from 'sonner';

import type { Interaction } from '@/types';

/**
 * Fires a one-shot sonner toast on the none→pending edge for each PREVIOUSLY
 * UNSEEN credential request (ENG-3976 active-notify). A seen-key ref makes it
 * fire exactly once per request: poll echoes, WS re-delivery, and unrelated
 * re-renders never re-toast (interaction keys are `#interaction#{conv}#{req}`,
 * unique and never reused, so the set needs no pruning).
 *
 * The toast is a STANDARD sonner action toast (role="status", non-modal) — it
 * carries no `alertdialog` semantics (uiux-critic fix). Its "Open" action
 * deep-links to the request via `onOpen(key)`.
 *
 * @param pendingInteractions all pending requests across the conversation tree
 * @param labelFor            resolves a conversationId to its display name
 * @param onOpen              opens the credential drawer to a specific request
 */
export function useInteractionToast(
  pendingInteractions: Interaction[],
  labelFor: (conversationId: string) => string,
  onOpen: (requestKey: string) => void
) {
  const seenRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    for (const interaction of pendingInteractions) {
      if (seenRef.current.has(interaction.key)) continue;
      seenRef.current.add(interaction.key);
      toast(`${labelFor(interaction.conversationId)} needs credentials`, {
        description: 'Open the credential requests to respond.',
        action: {
          label: 'Open',
          onClick: () => onOpen(interaction.key),
        },
      });
    }
  }, [pendingInteractions, labelFor, onOpen]);
}
